import { FiltersDefinition, SortingsDefinition } from '@factorialco/f0-react'
import { OneDataCollection, SummariesDefinition } from '@factorialco/f0-react/dist/experimental'
import { HeadcountSummariesDocument } from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import type { HeadcountRow } from 'modules/planning/roles/base/columnDefinition'
import { getColumnDefinitionByRole } from 'modules/planning/roles'
import type { PeriodRoleContext } from 'modules/planning/roles'

const defaultRow: HeadcountRow = {
  parentWithChildren: false,
  type: '',
  name: '',
  directManager: '',
  role: '',
  level: '',
  salary: '',
  workingTime: '',
  startDate: '',
  endDate: undefined,
}

export const HeadcountEmployeeSheet = ({
  periodId,
  roleContext,
}: {
  periodId: string
  roleContext: PeriodRoleContext | undefined
}) => {
  const columns = getColumnDefinitionByRole(roleContext?.higherRole)(
    roleContext ?? { higherRole: 'viewer', roles: [] }
  )

  const source = useGraphqlDataCollectionSource<
    HeadcountRow,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    typeof HeadcountSummariesDocument
  >(
    {
      document: HeadcountSummariesDocument,
      mapData: (data) => {
        const summaries = data.planning.headcountSummariesConnection.nodes
        const newHeadcountCount = summaries[0]?.newHeadcountCount ?? 0
        const actualHeadcountCount = summaries[0]?.actualHeadcountCount ?? 0

        return [
          {
            ...defaultRow,
            parentWithChildren: newHeadcountCount > 0,
            type: i18n.t('planning.employee_sheet.new_headcount'),
            name: `count ${newHeadcountCount}`,
            salary: `sum ${summaries[0]?.newHeadcountTotalMoney ?? '0'}`,
          },
          {
            ...defaultRow,
            parentWithChildren: actualHeadcountCount > 0,
            type: i18n.t('planning.employee_sheet.actual_headcount'),
            name: `count ${actualHeadcountCount}`,
            salary: `sum ${summaries[0]?.actualHeadcountTotalMoney ?? '0'}`,
          },
        ]
      },
      mapVariables: () => ({
        planningPeriodId: String(periodId),
      }),
      mapPagination: () => ({ total: 2 }),
      paginationType: 'pages',
      search: { enabled: true },
      itemsWithChildren: (item) => item.parentWithChildren,
    },
    [periodId]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table' as const,
          options: {
            columns,
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.employee_sheet.actual_headcount_empty.title'),
          description: i18n.t('planning.employee_sheet.actual_headcount_empty.description'),
        },
      }}
    />
  )
}
